import { ProcessedRoute } from './types';
import { useLanguage } from '../../../contexts/language';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useIotsApi } from '../../../contexts/iotsApi';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

export interface RescindRouteConfirmationDialogProps {
  contractId: string;
  route: ProcessedRoute | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const RescindRouteConfirmationDialog = ({
  isLoading,
  onClose,
  onConfirm,
  contractId,
  route,
}: RescindRouteConfirmationDialogProps) => {
  const { t } = useLanguage();
  const { rescindContractRoute } = useIotsApi();
  const rescindRouteMutation = useMutation({
    mutationFn: (routeId: string) => rescindContractRoute(contractId, routeId),
  });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  return (
    <ConfirmationDialog
      open={Boolean(route)}
      onClose={onClose}
      onConfirm={() => {
        if (route) {
          rescindRouteMutation.mutate(route.id, {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.contract-route-rescision-success'),
              });
              onConfirm();
              onClose();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.contract-route-rescision-error'),
                ),
              });
              rescindRouteMutation.reset();
            },
          });
        }
      }}
      loading={isLoading}
      title={t('content.contract-route-rescision-confirmation.title')}
    >
      <ConsequencesList
        action={t('content.contract-route-rescision-confirmation.action')}
        list={[
          t(
            'content.contract-route-rescision-confirmation.bullet-points.end-date-will-be-today',
          ),
          t(
            'content.contract-route-rescision-confirmation.bullet-points.older-journeys-are-unaffected',
          ),
        ]}
      />
    </ConfirmationDialog>
  );
};

export default RescindRouteConfirmationDialog;
